import { BotData, BotType, ClientData } from "@shared/types";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { MAX_FETCH_ALL_ACCOUNT_AMOUNTS } from "@shared/features/bots/BotForm/common/constants";
import useMainCoinsAvailableAmounts from "@shared/utils/hooks/useMainCoinsAvailableAmounts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useClientsData } from "@shared/utils/hooks/misc";

interface useBotFormAccountAmountsProps {
  botType?: BotType;
  botFormType?: BotFormType;
  account?: Pick<ClientData, "client_id" | "exchange">;
  symbol?: string;
  botsData?: BotData[];
  skip?: boolean;
}

export const useBotFormAccountAmounts = ({
  botFormType,
  account,
  botsData,
  botType: propsBotType,
  symbol,
  skip: propsSkip,
}: useBotFormAccountAmountsProps) => {
  const { botType: formBotType, isChannelConfiguration } =
    useContext(BotFormContext) || {};
  const isGroupOverview = !!useContext(GroupOverviewContext);
  const isOnboarding = !!useContext(OnboardingContext);
  const botType = propsBotType ?? formBotType;
  const { activeClientsData, getClientsListStatus } = useClientsData();
  const isSignalsBot = !botType || botType === BotType.SIGNAL;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const accountIds = botsData?.map((bot) => bot.client_id);
  const shouldFetchAllAccountsAmounts =
    isGroupOverview ||
    isBulkCreate ||
    (!!activeClientsData &&
      activeClientsData.length <= MAX_FETCH_ALL_ACCOUNT_AMOUNTS);

  const getSkipAccountAmounts = () => {
    if (isBulkEdit) return !accountIds?.length;
    return (
      isChannelConfiguration ||
      !getClientsListStatus.isSuccess ||
      !activeClientsData?.length ||
      (!shouldFetchAllAccountsAmounts && !account)
    );
  };

  const getAccountsArgs = () => {
    if (accountIds) return { client_ids: accountIds };
    else if (shouldFetchAllAccountsAmounts) return;
    return account ? { client_id: account.client_id } : skipToken;
  };

  const skip = propsSkip || getSkipAccountAmounts();
  return useMainCoinsAvailableAmounts({
    symbol,
    exchange: account?.exchange,
    accountId: account?.client_id,
    accountsArgs: getAccountsArgs(),
    withTotal: isSignalsBot || isOnboarding,
    skip,
  });
};
